/** Monthly dues (No-Shave): the secretary's month view, and each employee's own status. */
import { useState } from 'react';
import { api } from '../shared/api.ts';
import { Badge, Card, ErrorBox, Notice, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { KPI } from './common.tsx';

const monthName = (m: string) => new Date(`${m}-15T12:00:00`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
const shift = (m: string, by: number) => {
  const d = new Date(`${m}-15T12:00:00`);
  d.setMonth(d.getMonth() + by);
  return d.toISOString().slice(0, 7);
};
const money = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

function Settings({ s, onDone }: { s: any; onDone: () => void }) {
  const [f, setF] = useState(s);
  const a = useAction();
  return (
    <Card title="Program settings">
      <div className="grid-3">
        <label className="check" style={{ alignSelf: 'end' }}>
          <input type="checkbox" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} /> Turned on
        </label>
        <label>
          Name
          <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </label>
        <label>
          Amount ($ a month)
          <input type="number" min="0" step="1" value={f.amount} onChange={(e) => setF({ ...f, amount: Number(e.target.value) })} />
        </label>
        <label>
          Due on day
          <input type="number" min="1" max="28" value={f.dueDay} onChange={(e) => setF({ ...f, dueDay: Number(e.target.value) })} />
        </label>
        <label>
          Remind on Home from day
          <input type="number" min="1" max="28" value={f.remindDay} onChange={(e) => setF({ ...f, remindDay: Number(e.target.value) })} />
        </label>
      </div>
      <ErrorBox error={a.error} />
      <div>
        <button className="btn primary" disabled={a.busy} onClick={() => a.run(async () => (await api.post('/api/dues/settings', f), onDone()))}>
          Save settings
        </button>
      </div>
    </Card>
  );
}

export function DuesPage() {
  const [m, setM] = useState<string | null>(null);
  const r = useLoad(() => api.get<any>(`/api/dues${m ? `?month=${m}` : ''}`), [m]);
  const a = useAction();
  const [showSettings, setShowSettings] = useState(false);
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const { settings: s, people, summary, month } = r.data;
  const setIn = (userId: string, participates: boolean) => a.run(async () => (await api.post(`/api/dues/members/${userId}`, { participates }), r.reload()));
  const pay = (userId: string) => a.run(async () => (await api.post('/api/dues/payments', { userId, month }), r.reload()));
  const undo = (id: string) => window.confirm('Undo this payment?') && a.run(async () => (await api.post(`/api/dues/payments/${id}/void`), r.reload()));
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Agency"
        title={s.name}
        sub={`${money(s.amount)} a month, due the ${s.dueDay}th. Mark who’s in, and check people off as they pay.`}
        actions={
          <div className="row gap-sm">
            <a className="btn" href={`/api/dues/export?month=${month}`}>
              Excel
            </a>
            {r.data.canSettings && (
              <button className="btn" onClick={() => setShowSettings(!showSettings)}>
                Settings
              </button>
            )}
          </div>
        }
      />
      {!s.enabled && <Notice tone="warn">The program is turned off — no one is reminded. Turn it on under Settings.</Notice>}
      {(showSettings || !s.enabled) && r.data.canSettings && <Settings s={s} onDone={() => (setShowSettings(false), r.reload())} />}
      <div className="row between wrap gap-sm">
        <div className="row gap-sm">
          <button type="button" className="btn sm ghost" aria-label="Previous month" onClick={() => setM(shift(month, -1))}>
            ‹
          </button>
          <strong style={{ minWidth: 150, textAlign: 'center' }}>{monthName(month)}</strong>
          <button type="button" className="btn sm ghost" aria-label="Next month" onClick={() => setM(shift(month, 1))}>
            ›
          </button>
        </div>
        <div className="ops-kpis dues-kpis">
          <KPI n={`${summary.paid} / ${summary.in}`} label="paid" tone={summary.unpaid ? 'warn' : 'ok'} />
          <KPI n={money(summary.collected)} label={`of ${money(summary.expected)}`} />
          {summary.notSet > 0 && <KPI n={summary.notSet} label="not set yet" tone="warn" />}
        </div>
      </div>
      <ErrorBox error={a.error} />
      <div className="card table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>In the program?</th>
              <th>{monthName(month)}</th>
            </tr>
          </thead>
          <tbody>
            {people.map((p: any) => (
              <tr key={p.user_id}>
                <td>
                  <strong>{p.name}</strong>
                  <div className="small muted">{[p.call_number, p.unit].filter(Boolean).join(' · ')}</div>
                </td>
                <td>
                  <div className="seg-ctl sm" role="radiogroup" aria-label={`${p.name}: in the program`}>
                    <button type="button" role="radio" aria-checked={p.participates === true} className={p.participates === true ? 'on' : ''} disabled={a.busy} onClick={() => setIn(p.user_id, true)}>
                      In
                    </button>
                    <button type="button" role="radio" aria-checked={p.participates === false} className={p.participates === false ? 'on' : ''} disabled={a.busy} onClick={() => setIn(p.user_id, false)}>
                      Out
                    </button>
                  </div>
                  {p.participates == null && <div className="small warn-text">Not set</div>}
                </td>
                <td>
                  {!p.participates ? (
                    <span className="muted">—</span>
                  ) : p.payment_id ? (
                    <span>
                      <Badge tone="ok">Paid {new Date(`${p.paid_on}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</Badge>{' '}
                      <span className="small muted">{p.recorded_by_name}</span>{' '}
                      <button type="button" className="btn link small" onClick={() => undo(p.payment_id)}>
                        Undo
                      </button>
                    </span>
                  ) : (
                    <button type="button" className="btn sm primary" disabled={a.busy} onClick={() => pay(p.user_id)}>
                      Mark paid
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function MyDuesPage() {
  const r = useLoad(() => api.get<any>('/api/dues/me'));
  const a = useAction();
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  if (!r.data.enabled)
    return (
      <div className="ops stack">
        <PageHeader title="Monthly dues" />
        <p className="muted">Your agency doesn’t run a dues program in BlueHQ.</p>
      </div>
    );
  const { settings: s, participates, paidThisMonth, payments, month } = r.data;
  return (
    <div className="ops stack">
      <PageHeader crumb="Me" title={s.name} sub={`${money(s.amount)} a month, due the ${s.dueDay}th — paid to the secretary.`} />
      <ErrorBox error={a.error} />
      {participates == null ? (
        <Card title={`Are you in ${s.name}?`}>
          <p className="small">Everyone has to answer. After this, the secretary makes any change.</p>
          <div className="row gap-sm">
            <button className="btn primary" disabled={a.busy} onClick={() => a.run(async () => (await api.post('/api/dues/me', { participates: true }), r.reload()))}>
              Yes, I’m in
            </button>
            <button className="btn" disabled={a.busy} onClick={() => a.run(async () => (await api.post('/api/dues/me', { participates: false }), r.reload()))}>
              No
            </button>
          </div>
        </Card>
      ) : !participates ? (
        <Notice tone="info">You’re not in {s.name}. Ask the secretary if you want to join.</Notice>
      ) : paidThisMonth ? (
        <Notice tone="ok">✓ You’re paid for {monthName(month)}.</Notice>
      ) : (
        <Notice tone="warn">
          {money(s.amount)} for {monthName(month)} is due the {s.dueDay}th. Pay the secretary; it comes off your Home when they mark you paid.
        </Notice>
      )}
      {payments.length > 0 && (
        <Card title="Your payments">
          <ul className="plain">
            {payments.map((p: any) => (
              <li key={p.period}>
                {monthName(p.period)} · {money(p.amount)} · paid {new Date(`${p.paid_on}T12:00:00`).toLocaleDateString('en-US')}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
